import { useContext, useState, useEffect, useMemo } from "react";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { EditOutlined } from "@ant-design/icons";
import {
  Typography,
  Table,
  Button,
  Form,
  Input,
  DatePicker,
  Select,
  Space,
  Modal,
} from "antd";

const { Option } = Select;

export default function ViewHoliday() {
  const { backUrl, authData } = useContext(AuthContext);
  const [holidayData, setHolidayData] = useState([]);
  const [holidayOptions, setHolidayOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Filters
  const [selectedYear, setSelectedYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [holidayName, setHolidayName] = useState(null);

  // Status tab: "pending" | "approved"
  const [statusTab, setStatusTab] = useState("pending");

  // Row selection (for approving)
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Fetch holidays from backend
  const fetchHolidays = async ({
    yearOverride,
    monthOverride,
    fromOverride,
    toOverride,
    nameOverride,
  } = {}) => {
    setLoading(true);
    try {
      const y = yearOverride ?? selectedYear;
      const m = monthOverride ?? month;
      const f = fromOverride ?? fromDate;
      const t = toOverride ?? toDate;
      const n = nameOverride ?? holidayName;

      const params = {};
      if (y) params.from_year = y;
      if (m) params.month = m;
      if (f) params.from_date = f.format("YYYY-MM-DD");
      if (t) params.to_date = t.format("YYYY-MM-DD");
      if (n) params.holiday_name = n;

      const res = await axios.get(`${backUrl}/api/hr/holidays/`, {
        headers: { Authorization: `Bearer ${authData.access}` },
        params,
      });

      const list = res.data.results ? res.data.results : res.data;
      setHolidayData(
        list.map((item) => ({
          ...item,
          holiday_date: item.holiday_date
            ? dayjs(item.holiday_date).format("YYYY-MM-DD")
            : null,
        }))
      );

      // Populate holiday name options
      const uniqueNames = [
        ...new Set(list.map((h) => h.holiday_name).filter(Boolean)),
      ];
      setHolidayOptions(uniqueNames);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch holidays!");
      setHolidayData([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on filter change
  useEffect(() => {
    if ((fromDate && !toDate) || (!fromDate && toDate)) return;
    fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, month, fromDate, toDate, holidayName]);

  // Clear selection whenever the Pending/Approved tab changes
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [statusTab]);

  // Month picker change
  const onMonthChange = (date) => {
    if (!date) {
      setMonth(null);
      return;
    }
    setMonth(date.month() + 1);
    if (!selectedYear) setSelectedYear(String(date.year()));
  };

  /** Derived: split holidays into pending / approved (same condition as AddHoliday) */
  const isApproved = (record) =>
    Boolean(record.approved_by_name) || Boolean(record.approved_on);

  const pendingHolidays = useMemo(
    () => holidayData.filter((item) => !isApproved(item)),
    [holidayData]
  );

  const approvedHolidays = useMemo(
    () => holidayData.filter((item) => isApproved(item)),
    [holidayData]
  );

  const displayedHolidays = useMemo(
    () => (statusTab === "pending" ? pendingHolidays : approvedHolidays),
    [statusTab, pendingHolidays, approvedHolidays]
  );

  // Approve selected holidays
  const handleApproveSelected = async () => {
    if (selectedRowKeys.length === 0) return;

    try {
      setLoading(true);

      const res = await axios.post(
        `${backUrl}/api/hr/holidays/approve-holidays/`,
        { holiday_ids: selectedRowKeys },
        {
          headers: { Authorization: `Bearer ${authData?.access}` },
        }
      );

      if (res.data?.message) {
        toast.success(res.data.message);
      } else {
        toast.success("Selected holidays approved!");
      }

      setSelectedRowKeys([]);
      fetchHolidays();
    } catch (err) {
      console.error("Approval error:", err);

      if (err.response) {
        const data = err.response.data;

        if (data.message) {
          toast.error(data.message);
        } else if (typeof data === "object") {
          Object.entries(data).forEach(([key, value]) => {
            toast.error(`${key}: ${Array.isArray(value) ? value.join(", ") : value}`);
          });
        } else {
          toast.error(data.toString());
        }
      } else if (err.request) {
        toast.error("No response from server!");
      } else {
        toast.error(err.message || "Failed to approve holidays!");
      }
    } finally {
      setLoading(false);
    }
  };

  // Filters UI
  const FilterHolidays = () => {
    const monthValue = month
      ? dayjs(`${selectedYear || dayjs().year()}-${String(month).padStart(2, "0")}`, "YYYY-MM")
      : null;

    return (
      <div className="d-flex justify-content-center align-items-center pt-2" style={{ gap: 12, flexWrap: "wrap" }}>
        {/* Year */}
        <DatePicker
          picker="year"
          placeholder="Select Year"
          value={selectedYear ? dayjs(selectedYear, "YYYY") : undefined}
          onChange={(date, dateString) => setSelectedYear(dateString || null)}
          style={{ width: 180 }}
          allowClear
        />

        {/* Month */}
        <DatePicker
          picker="month"
          placeholder="Select Month"
          value={monthValue || undefined}
          onChange={onMonthChange}
          style={{ width: 180 }}
          allowClear
        />

        {/* From Date */}
        <DatePicker
          placeholder="From Date"
          format="DD/MM/YYYY"
          value={fromDate || undefined}
          onChange={(date) => setFromDate(date)}
          style={{ width: 180 }}
          allowClear
        />

        {/* To Date */}
        <DatePicker
          placeholder="To Date"
          format="DD/MM/YYYY"
          value={toDate || undefined}
          onChange={(date) => setToDate(date)}
          style={{ width: 180 }}
          allowClear
        />

        {/* Holiday Name */}
        <Select
          showSearch
          allowClear
          placeholder="Select Holiday Name"
          value={holidayName || undefined}
          onChange={(value) => setHolidayName(value || null)}
          style={{ width: 220 }}
          filterOption={(input, option) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())
          }
        >
          {holidayOptions.map((name) => (
            <Option key={name} value={name}>
              {name}
            </Option>
          ))}
        </Select>
      </div>
    );
  };

  // Pending / Approved status tabs
  const StatusTabs = () => {
    const baseStyle = {
      borderRadius: 8,
      fontWeight: 600,
      minWidth: 140,
    };

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Button
          style={{
            ...baseStyle,
            backgroundColor: statusTab === "pending" ? "#0e497b" : "#fff",
            color: statusTab === "pending" ? "#fff" : "#0e497b",
            border: "1px solid #0e497b",
          }}
          onClick={() => setStatusTab("pending")}
        >
          Pending ({pendingHolidays.length})
        </Button>

        <Button
          style={{
            ...baseStyle,
            backgroundColor: statusTab === "approved" ? "#0e497b" : "#fff",
            color: statusTab === "approved" ? "#fff" : "#0e497b",
            border: "1px solid #0e497b",
          }}
          onClick={() => setStatusTab("approved")}
        >
          Approved ({approvedHolidays.length})
        </Button>
      </div>
    );
  };

  // Edit holidays
  const EditHolidays = () => {
    const [isEdit, setIsEdit] = useState(false);
    const [editData, setEditData] = useState(null);
    const [form] = Form.useForm();

    const handleEdit = (record) => {
      setEditData(record);
      setIsEdit(true);
      form.setFieldsValue({
        holiday_name: record.holiday_name,
        holiday_date: record.holiday_date ? dayjs(record.holiday_date, "YYYY-MM-DD") : null,
        year: record.year,
        holiday_day: record.holiday_day,
      });
    };

    const handleUpdate = async () => {
      try {
        const values = await form.validateFields();
        const payload = {
          ...values,
          id: editData.id,
          holiday_date: values.holiday_date.format("YYYY-MM-DD"),
        };

        await axios.put(`${backUrl}/api/hr/holidays/${editData.id}/`, payload, {
          headers: { Authorization: `Bearer ${authData.access}` },
        });

        toast.success("Holiday updated!");
        setIsEdit(false);
        fetchHolidays();
      } catch (err) {
        console.error(err);
        toast.error("Failed to update holiday!");
      }
    };

    const columns = [
      { title: "Year", dataIndex: "year", key: "year" },
      {
        title: "Month",
        dataIndex: "holiday_date",
        key: "month",
        render: (date) => (date ? dayjs(date).format("MMMM") : "-"),
      },
      { title: "Day", dataIndex: "holiday_day", key: "holiday_day" },
      {
        title: "Date",
        dataIndex: "holiday_date",
        key: "holiday_date",
        render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
      },
      { title: "Name", dataIndex: "holiday_name", key: "holiday_name" },
      ...(statusTab === "approved"
        ? [
          {
            title: "Approved By",
            dataIndex: "approved_by_name",
            key: "approved_by_name",
            render: (val) => val || "-",
          },
          {
            title: "Approved On",
            dataIndex: "approved_on",
            key: "approved_on",
            render: (val) =>
              val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-",
          },
        ]
        : []),
      {
        title: "Update",
        key: "update",
        render: (_, record) => (
          <EditOutlined
            style={{ color: "#0e497b", fontSize: 18, cursor: "pointer" }}
            onClick={() => handleEdit(record)}
          />
        ),
      },
    ];

    const rowSelection = {
      selectedRowKeys,
      preserveSelectedRowKeys: true,
      onChange: (newKeys) => {
        setSelectedRowKeys(newKeys);
      },
    };

    return (
      <>
        <center>
          <FilterHolidays />
        </center>
        <br />
        <StatusTabs />

        {statusTab === "pending" && selectedRowKeys.length !== 0 && (
          <Button
            type="primary"
            style={{ backgroundColor: "#149a0cff", color: "#fff", marginBottom: 12 }}
            disabled={selectedRowKeys.length === 0}
            onClick={handleApproveSelected}
          >
            Approve Selected
          </Button>
        )}

        <Table
          rowSelection={statusTab === "pending" ? rowSelection : undefined}
          columns={columns}
          dataSource={displayedHolidays}
          rowKey="id"
          loading={loading}
          bordered
          pagination={pagination}
          onChange={(newPagination) => {
            setPagination(newPagination);
          }}
          components={{
            header: {
              cell: (props) => (
                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                  {props.children}
                </th>
              ),
            },
          }}
          scroll={{ x: "max-content" }}
        />
        <Modal
          title="Edit Holiday"
          open={isEdit}
          onCancel={() => setIsEdit(false)}
          footer={[
            <Button key="cancel" onClick={() => setIsEdit(false)}>
              Cancel
            </Button>,
            <Button
              key="update"
              type="primary"
              style={{ backgroundColor: "#0e497b", color: "white" }}
              onClick={handleUpdate}
            >
              Update
            </Button>,
          ]}
          centered
          destroyOnClose
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="holiday_name"
              label="Holiday Name"
              rules={[{ required: true, message: "Please enter holiday name" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="holiday_date"
              label="Holiday Date"
              rules={[{ required: true, message: "Please select holiday date" }]}
            >
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="year" label="Year">
              <Input disabled />
            </Form.Item>
            <Form.Item name="holiday_day" label="Holiday Day">
              <Input disabled />
            </Form.Item>
          </Form>
        </Modal>
      </>
    );
  };

  return (
    <div style={{ padding: 20 }}>
      <center>
        <h5>Holiday List</h5>
      </center>
      <EditHolidays />
    </div>
  );
}